// API client for RICHGURU AI Platform
// PURGED: All Synthetic/Mock logic has been removed as per Sovereign Intelligence Protocol.

// ─── HTTPS Enforcement ────────────────────────────────────────────────────────
// Force HTTPS in production to prevent Mixed Content browser blocks.
// `/api/v1` (relative) is the correct production setting; this guard handles
// any absolute URL that accidentally uses http://.
function resolveBaseURL(raw: string): string {
  // Relative path — safe, browser inherits the page protocol (always https in prod)
  if (raw.startsWith('/')) return raw;
  // Absolute URL — upgrade to HTTPS if page is HTTPS
  if (
    typeof window !== 'undefined' &&
    window.location.protocol === 'https:' &&
    raw.startsWith('http://')
  ) {
    console.warn('[API] Upgrading insecure API base URL to HTTPS:', raw);
    return raw.replace('http://', 'https://');
  }
  return raw.replace(/\/$/, ''); // remove trailing slash
}

const API_BASE_URL = resolveBaseURL(
  import.meta.env.VITE_API_BASE_URL || '/api/v1'
);


export interface ApiResponse<T = unknown> {
  data?: T;
  error?: string;
  status: number;
}

class ApiClient {
  private baseURL: string;
  private token: string | null = null;

  constructor(baseURL: string) {
    this.baseURL = baseURL;
    this.loadToken();
  }

  private loadToken() {
    if (typeof window !== 'undefined') {
      this.token = localStorage.getItem('auth_token');
    }
  }

  private setToken(token: string, refreshToken?: string) {
    this.token = token;
    if (typeof window !== 'undefined') {
      localStorage.setItem('auth_token', token);
      if (refreshToken) {
        localStorage.setItem('refresh_token', refreshToken);
      }
    }
  }

  private clearToken() {
    this.token = null;
    if (typeof window !== 'undefined') {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('refresh_token');
    }
  }

  private async refreshToken(): Promise<boolean> {
    const refreshToken = localStorage.getItem('refresh_token');
    if (!refreshToken) return false;

    try {
      const response = await fetch(`${this.baseURL}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });

      if (response.ok) {
        const data = await response.json();
        this.setToken(data.access_token, data.refresh_token);
        return true;
      }
    } catch (err) {
      console.error('Session Recovery Failed');
    }

    this.clearToken();
    return false;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
    retryCount = 0
  ): Promise<ApiResponse<T>> {
    const url = `${this.baseURL}${endpoint}`;
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string>),
    };

    if (this.token) {
      headers.Authorization = `Bearer ${this.token}`;
    }

    try {
      const response = await fetch(url, {
        ...options,
        headers,
      });

      // Session Recovery Logic: Detecting Expiry
      if (response.status === 401 && retryCount === 0) {
        const success = await this.refreshToken();
        if (success) {
          return this.request(endpoint, options, 1);
        }
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        return {
          error: errorData.detail || errorData.error || `Institutional Link Error: ${response.status}`,
          status: response.status,
        };
      }

      const data = await response.json();
      return {
        data,
        status: response.status,
      };
    } catch (error: any) {
      return {
        error: `Institutional Link Failed: ${error.message || 'System error'}`,
        status: 500,
      };
    }
  }

  // Authentication
  async login(email: string, password: string): Promise<ApiResponse<{ access_token: string; refresh_token: string }>> {
    const response = await this.request<{ access_token: string; refresh_token: string }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });

    if (response.data?.access_token) {
      this.setToken(response.data.access_token, response.data.refresh_token);
    }

    return response;
  }

  async register(userData: {
    email: string;
    password: string;
    first_name: string;
    last_name: string;
    wallet_address?: string;
  }): Promise<ApiResponse<{ access_token: string; refresh_token: string }>> {
    const response = await this.request<{ access_token: string; refresh_token: string }>('/auth/register', {
      method: 'POST',
      body: JSON.stringify(userData),
    });

    if (response.data?.access_token) {
      this.setToken(response.data.access_token, response.data.refresh_token);
    }

    return response;
  }

  async logout(): Promise<ApiResponse> {
    const response = await this.request('/auth/logout', { method: 'POST' });
    this.clearToken();
    return response;
  }

  async getCurrentUser(): Promise<ApiResponse<any>> {
    return this.request('/auth/me');
  }

  // User Management
  async updateUserProfile(profileData: {
    first_name?: string;
    last_name?: string;
    wallet_address?: string;
  }): Promise<ApiResponse<unknown>> {
    return this.request('/users/profile', {
      method: 'PUT',
      body: JSON.stringify(profileData),
    });
  }

  // Portfolio Management
  async getPortfolios(page: number = 1, size: number = 10): Promise<ApiResponse<unknown>> {
    return this.request(`/portfolios?page=${page}&size=${size}`);
  }

  async createPortfolio(portfolioData: {
    name: string;
    description?: string;
    risk_profile: string;
    initial_balance: number;
  }): Promise<ApiResponse<unknown>> {
    return this.request('/portfolios', {
      method: 'POST',
      body: JSON.stringify(portfolioData),
    });
  }

  async getPortfolio(portfolioId: string): Promise<ApiResponse<unknown>> {
    return this.request(`/portfolios/${portfolioId}`);
  }

  async updatePortfolio(portfolioId: string, portfolioData: {
    name?: string;
    description?: string;
    risk_profile?: string;
  }): Promise<ApiResponse<unknown>> {
    return this.request(`/portfolios/${portfolioId}`, {
      method: 'PUT',
      body: JSON.stringify(portfolioData),
    });
  }

  async deletePortfolio(portfolioId: string): Promise<ApiResponse<unknown>> {
    return this.request(`/portfolios/${portfolioId}`, { method: 'DELETE' });
  }

  // Trading
  async placeTradeOrder(orderData: {
    symbol: string;
    side: string;
    quantity: number;
    order_type: string;
    price?: number;
    portfolio_id: string;
  }): Promise<ApiResponse<unknown>> {
    return this.request('/trading/orders', {
      method: 'POST',
      body: JSON.stringify(orderData),
    });
  }

  async getTradeOrders(): Promise<ApiResponse<unknown>> {
    return this.request('/trading/orders');
  }

  async getPositions(broker: 'alpaca' | 'oanda' = 'alpaca'): Promise<ApiResponse<unknown>> {
    // Route: /trading/{broker}/positions is registered (not the generic /trading/positions)
    return this.request(`/trading/${broker}/positions`);
  }

  // AI Signals
  async getAISignals(): Promise<ApiResponse<unknown>> {
    return this.request('/ai/signals');
  }

  async getAIAnalysis(symbol: string): Promise<ApiResponse<unknown>> {
    return this.request(`/ai/analysis/${symbol}`);
  }

  // Market Data
  async getMarketData(symbols: string[] = ['AAPL', 'TSLA', 'BTC', 'GOOGL', 'MSFT']): Promise<ApiResponse<unknown>> {
    return this.request(`/market-data/quotes?symbols=${symbols.join(',')}`);
  }

  async getMarketQuote(symbol: string): Promise<ApiResponse<any>> {
    return this.request(`/market-data/quotes/${symbol}`);
  }

  async getWatchlistQuotes(): Promise<ApiResponse<any>> {
    return this.request('/market-data/watchlist');
  }

  // Advanced Indicators
  async getAdvancedIndicators(symbol: string, indicators: string[] = ['RSI', 'MACD', 'BB', 'SMA', 'EMA']): Promise<ApiResponse<any>> {
    return this.request('/indicators/advanced', {
      method: 'POST',
      body: JSON.stringify({ symbol, indicators }),
    });
  }

  // A2A (Agent-to-Agent)
  async getA2AMessages(): Promise<ApiResponse<unknown>> {
    return this.request('/a2a/messages');
  }

  async sendA2AMessage(data: any) {
    return this.request('/a2a/messages', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getA2AAgents() {
    return this.request('/a2a/agents');
  }

  async createA2AStrategy(data: any) {
    return this.request('/a2a/strategies', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getAgentStatus(): Promise<ApiResponse<unknown>> {
    return this.request('/a2a/status');
  }

  async getA2AStrategies(): Promise<ApiResponse<unknown>> {
    return this.request('/a2a/strategies');
  }

  // AI Thought Streaming (SSE)
  streamNeuralThoughts(symbol: string, onUpdate: (thought: string) => void): () => void {
    const url = `${this.baseURL}/ai/thoughts/stream/${symbol}`;
    const eventSource = new EventSource(url);

    eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.thought) {
          onUpdate(data.thought);
        }
      } catch (err) {
        console.error('Thought Stream Parse Error');
      }
    };

    eventSource.onerror = () => {
      console.warn('Neural Stream Interrupted. Re-syncing...');
      eventSource.close();
    };

    return () => eventSource.close();
  }

  // Health Check
  async healthCheck(): Promise<ApiResponse<any>> {
    return this.request('/health');
  }

  // Utility Methods
  isAuthenticated(): boolean {
    return !!this.token;
  }

  getToken(): string | null {
    return this.token;
  }
}

// Create and export a singleton instance
export const apiClient = new ApiClient(API_BASE_URL);
export default apiClient;